import { FC, useState } from "react";
import {
    differenceInCalendarDays,
    format,
    fromUnixTime,
    getUnixTime,
} from "date-fns";
import { FLAT_RATE_PLANS, PLAN_CATALOG_VERSION } from "@common/flatRateBilling";
import { currencyFormatter } from "@common/helpers/currency";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { fromIntToMonetary } from "@common/helpers/monetary";
import { pluralizeWithCount } from "@common/helpers/string";
import { ClientPricing } from "@hooks/billing/useClientPricing";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import {
    estimateProratedChargeCents,
    resolveBillingCycle,
} from "./billingPreview";
import { getOrganizationOwnedSeatMonthlyAmountCents } from "./organizationOwnedSeatPricing";
import styles from "./V3BillingPreview.module.scss";

interface V3BillingPreviewProps {
    clientPricing: ClientPricing;
    plan: FLAT_RATE_PLANS;
    isOrgOwned: boolean;
}

interface PreviewContent {
    proratedAmountCents: number;
    monthlyAmountCents: number;
    daysRemaining: number;
    cycleEndsAtSeconds: number;
}

const isSameContent = (a: PreviewContent, b: PreviewContent | null): boolean =>
    b != null &&
    a.proratedAmountCents === b.proratedAmountCents &&
    a.monthlyAmountCents === b.monthlyAmountCents &&
    a.daysRemaining === b.daysRemaining &&
    a.cycleEndsAtSeconds === b.cycleEndsAtSeconds;

const getPreviewContent = ({
    clientPricing,
    plan,
    currentPeriodStart,
    currentPeriodEnd,
    now,
}: {
    clientPricing: ClientPricing;
    plan: FLAT_RATE_PLANS;
    currentPeriodStart: number;
    currentPeriodEnd: number;
    now: Date;
}): PreviewContent => {
    const monthlyAmountCents = getOrganizationOwnedSeatMonthlyAmountCents({
        clientPricing,
        plan,
    });
    const cycle = resolveBillingCycle({
        periodStartsAt: fromUnixTime(currentPeriodStart),
        periodEndsAt: fromUnixTime(currentPeriodEnd),
        now,
    });

    return {
        monthlyAmountCents,
        proratedAmountCents: estimateProratedChargeCents({
            monthlyAmountCents,
            cycle,
            now,
        }),
        daysRemaining: differenceInCalendarDays(cycle.endsAt, now),
        cycleEndsAtSeconds: getUnixTime(cycle.endsAt),
    };
};

const formatCents = (cents: number): string =>
    currencyFormatter.format(fromIntToMonetary(cents));

export const V3BillingPreview: FC<V3BillingPreviewProps> = ({
    clientPricing,
    plan,
    isOrgOwned,
}) => {
    // oxlint-disable-next-line react/hook-use-state -- The preview's reference time is read once and never updated.
    const [now] = useState(() => new Date());
    const activeOrganization = useActiveOrganization();
    const billing = activeOrganization?.billing;
    const isV3 = billing?.planCatalogVersion === PLAN_CATALOG_VERSION.V3;
    const licensedPriceItem =
        billing?.subscriptionDetails?.licensedPriceItems?.[0];
    const shouldHidePreview =
        !isV3 ||
        !isOrgOwned ||
        plan === FLAT_RATE_PLANS.FREE ||
        !clientPricing.isOrganizationOwnedPricingReady ||
        licensedPriceItem == null;

    const liveContent = shouldHidePreview
        ? null
        : getPreviewContent({
              clientPricing,
              plan,
              currentPeriodStart: licensedPriceItem.currentPeriodStart,
              currentPeriodEnd: licensedPriceItem.currentPeriodEnd,
              now,
          });
    const [heldContent, setHeldContent] = useState(liveContent);

    if (liveContent != null && !isSameContent(liveContent, heldContent)) {
        setHeldContent(liveContent);
    }

    const content = liveContent ?? heldContent;

    return (
        <div
            className={styles.slot}
            data-open={liveContent == null ? undefined : ""}
            aria-hidden={liveContent == null}
        >
            <div className={styles.clip}>
                {content != null && (
                    <p className={styles.preview}>
                        <span>
                            You'll be billed{" "}
                            <strong>
                                {formatCents(content.proratedAmountCents)}
                            </strong>{" "}
                            today for the{" "}
                            {pluralizeWithCount("day", content.daysRemaining)}{" "}
                            left in this billing cycle, then{" "}
                            <strong>
                                {formatCents(content.monthlyAmountCents)}/mo
                            </strong>{" "}
                            from{" "}
                            {format(
                                fromUnixTime(content.cycleEndsAtSeconds),
                                fullDateFullYearFormat,
                            )}
                            .
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
};
